import { useMutation } from "@apollo/client";
import { compact, uniq } from "lodash";
import { useCallback, useMemo, useState } from "react";
import {
  AssessmentDeliveryState,
  AssessmentType,
  BatchAssessmentDeliveryActionMutationMutation,
  BatchAssessmentDeliveryActionMutationMutationVariables,
  BatchCalibrationAction,
  ComplianceProgramCalibrationStatus,
  ComplianceProgramCalibrationType,
  ExportPerformanceAssessmentCsvMutation,
  ExportPerformanceAssessmentCsvMutationVariables,
} from "types/graphql-schema";

import { getProgramUrl } from "@apps/programs/helpers";
import batchAssessmentDeliveryActionMutation from "@apps/reporting/graphql/batch-assessment-deliveries-action-mutation";
import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import Dropdown from "@components/dropdown/dropdown";
import { removeConsecutiveDividers } from "@components/dropdown/dropdown-helpers";
import Loading from "@components/loading/loading";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import useConfirm from "@helpers/hooks/use-confirm";

import PerformanceAssessmentReportContext from "../context/performance-assessment-report-context";
import exportPerformanceAssessmentCsvMutation from "../graphql/export-performance-assessment-csv-mutation";
import {
  getBatchCalibrationConfirmation,
  getBatchCalibrationSuccess,
} from "./assessment-report/confirmation-strings";
import PerformanceAssessmentChart from "./assessment-report/performance-assessment-chart";
import PerformanceAssessmentTable from "./assessment-report/performance-assessment-table";
import {
  filterPerformanceReportRows,
  getFilterByType,
  updateFilters,
} from "./assessment-report/report-filters/helpers";
import {
  AssessmentReportFilterMatchType,
  AssessmentReportFilterType,
} from "./assessment-report/report-filters/types";
import useAssessmentReport from "./assessment-report/use-assessment-report";
import CalibrationGroupsModal from "./calibration-groups/calibration-groups-modal";
import { useNoEmployeesSelectedDialog } from "./calibration-groups/hooks";
import { CalibrationGroupsModalView } from "./calibration-groups/types";
import useCalibrationGroupPicker from "./use-calibration-group-picker";
import ViewOptionsButton from "./view-options-button";

const PerformanceAssessmentReport = ({
  complianceProgramId,
  isInReporting,
  headerContent,
  embedded = false,
}: {
  complianceProgramId: number;
  isInReporting: boolean;
  headerContent: (reportSettings: JSX.Element) => JSX.Element;
  // When embedded in the Reviews page (no Reporting nav bar above), tighten the
  // top spacing: add padding above the header row and shrink the body offset.
  embedded?: boolean;
}) => {
  const currentUser = currentUserVar();
  const isAdmin = isAdminVar();
  const organization = currentOrganizationVar();
  const label = useLabel();

  const {
    chartLabels,
    setChartLabels,
    viewOptions,
    showViewOptionsSidebar,
    handleHideViewOptionsSidebar,
    handleShowViewOptionsSidebar,
    handleUpdateViewOptions,
    handleResetViewOptionsToBaseline,
    handleSaveAsNewView,
    handleSaveExistingView,
    handleDuplicateView,
    handleDeleteView,
    complianceProgram,
    isLoadingProgram,
    assessmentTemplate,
    questions,
    talentIndicatorQuestions,
    isSavingReportingView,
    isDeletingReportingView,
    ReportingViewDeletionConfirm,
    savedViewOptions,
    handleChangeSavedView,
    recentAnswerQuestionIds,
    prevAnswersByAssessmentId,
    handleResetViewOptionsToEmpty,
  } = useAssessmentReport(AssessmentType.Performance, complianceProgramId);
  const {
    calibrationGroupsEnabled,
    calibrationGroups,
    isLoadingCalibrationGroups,
    calibrationGroupOptions,
    selectedCalibrationGroupId,
    setSelectedCalibrationGroupId,
    userRows,
    calibrationGroupRows,
  } = useCalibrationGroupPicker({
    complianceProgramId,
    complianceProgram,
  });

  const {
    openModal: openCalibrationGroupNoEmployeesModal,
    InfoDialog: CalibrationGroupNoEmployeesInfoDialog,
  } = useNoEmployeesSelectedDialog();

  const [showCalibrationGroupsModal, setShowCalibrationGroupsModal] =
    useState<CalibrationGroupsModalView | null>(null);
  const [exportPerformanceAssessmentCsv, { loading: isExportingCsv }] =
    useMutation<
      ExportPerformanceAssessmentCsvMutation,
      ExportPerformanceAssessmentCsvMutationVariables
    >(exportPerformanceAssessmentCsvMutation);

  const handleExportCsv = useCallback(() => {
    const filterItems = viewOptions.filters.list;
    const userFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.USER,
    );
    const reportsToFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.REPORTS_TO,
    );
    const answerFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.ANSWER,
    );
    const talentIndicatorFilterList = filterItems
      .filter(
        (item) => item.type === AssessmentReportFilterType.TALENT_INDICATOR,
      )
      .map((item) => item.data);
    const teamFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.TEAM,
    );
    const enrollmentDateFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.ENROLLMENT_DATE,
    );
    const roleFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.ROLE,
    );
    const careerTrackFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.CAREER_TRACK,
    );
    const careerTrackLevelFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.CAREER_TRACK_LEVEL,
    );
    exportPerformanceAssessmentCsv({
      variables: {
        complianceProgramId: complianceProgramId,
        userIds: userFilterList.map(({ id }) => id),
        reportToIds: reportsToFilterList.map(({ id }) => id),
        teamIds: teamFilterList.map(({ id }) => id),
        roleIds: roleFilterList.map(({ id }) => id),
        roleLevels: careerTrackLevelFilterList,
        enrollmentDateFilter: enrollmentDateFilterList,
        careerTrackIds: careerTrackFilterList.map(({ id }) => id),
        answerFilters: answerFilterList,
        talentIndicatorFilters: talentIndicatorFilterList,
        matchAny:
          viewOptions.filters.type ===
          AssessmentReportFilterMatchType.MATCH_ANY,
      },
      onCompleted: () => {
        successNotificationVar({
          title: `CSV export will be emailed to ${currentUser.email}. The link will expire in two hours.`,
        });
      },
    });
  }, [
    viewOptions.filters,
    exportPerformanceAssessmentCsv,
    complianceProgramId,
    currentUser,
  ]);

  const filteredTableRows = useMemo(
    () =>
      filterPerformanceReportRows(calibrationGroupRows, viewOptions.filters),
    [viewOptions.filters, calibrationGroupRows],
  );

  const handleShowCalibrationGroupsList = useCallback(() => {
    setShowCalibrationGroupsModal(CalibrationGroupsModalView.LIST);
  }, []);
  const handleShowCalibrationGroupCreateNew = useCallback(() => {
    if (filteredTableRows.length > 0) {
      setShowCalibrationGroupsModal(CalibrationGroupsModalView.FORM);
    } else {
      openCalibrationGroupNoEmployeesModal();
    }
  }, [openCalibrationGroupNoEmployeesModal, filteredTableRows]);
  const handleCloseCalibrationGroupsModal = useCallback(() => {
    setShowCalibrationGroupsModal(null);
  }, []);

  const notCalibratedTargetIds = useMemo(
    () =>
      filteredTableRows
        .filter((row) => row.canCalibrateAnswer.permission)
        .filter(
          (row) =>
            row.calibrationStatus ===
              ComplianceProgramCalibrationStatus.NotStarted &&
            (complianceProgram?.calibration ===
              ComplianceProgramCalibrationType.PreCalibration ||
              (complianceProgram?.calibration ===
                ComplianceProgramCalibrationType.PostCalibration &&
                row.assessment)),
        )
        .map((row) => row.user.id),
    [filteredTableRows, complianceProgram?.calibration],
  );
  const calibratedNotApprovedTargetIds = useMemo(
    () =>
      filteredTableRows
        .filter(
          (row) => row.assessment?.assessmentDelivery?.canUpdate?.permission,
        )
        .filter(
          (row) =>
            row.calibrationStatus ===
              ComplianceProgramCalibrationStatus.Calibrated &&
            row.assessment &&
            row.assessment.assessmentDelivery?.state ===
              AssessmentDeliveryState.AwaitingAdminApproval,
        )
        .map((row) => row.user.id),
    [filteredTableRows],
  );
  const notStartedTargetIds = useMemo(
    () =>
      filteredTableRows
        .filter(
          (row) => row.assessment?.assessmentDelivery?.canUpdate?.permission,
        )
        .filter(
          (row) =>
            row.assessment?.assessmentDelivery?.state ===
            AssessmentDeliveryState.AwaitingAdminApproval,
        )
        .map((row) => row.user.id),
    [filteredTableRows],
  );

  const {
    confirm: confirmBatchAction,
    ConfirmationDialog: BatchActionConfirmationDialog,
    setTitle: setBatchActionConfirmationTitle,
    setMessage: setBatchActionConfirmationMessage,
  } = useConfirm("Confirm Calibration", "Confirm");

  const [
    batchAssessmentDeliveryAction,
    { loading: isRunningBatchDeliveryAction },
  ] = useMutation<
    BatchAssessmentDeliveryActionMutationMutation,
    BatchAssessmentDeliveryActionMutationMutationVariables
  >(batchAssessmentDeliveryActionMutation);

  const handleBatchCalibrationAction = useCallback(
    async (action: BatchCalibrationAction) => {
      const title =
        action === BatchCalibrationAction.MarkAsCalibrated
          ? "Confirm Calibration"
          : "Confirm Approval";
      const targetIds =
        action === BatchCalibrationAction.MarkAsCalibrated
          ? [...notCalibratedTargetIds]
          : action === BatchCalibrationAction.ApproveCalibrated
            ? [...calibratedNotApprovedTargetIds]
            : action === BatchCalibrationAction.ApproveAll
              ? uniq([...notStartedTargetIds])
              : [];

      const message = getBatchCalibrationConfirmation(action, {
        label,
        calibrationType: complianceProgram?.calibration,
        targetCount: targetIds.length,
      });

      setBatchActionConfirmationTitle(title);
      setBatchActionConfirmationMessage(message);
      const confirmed = await confirmBatchAction();
      if (confirmed) {
        batchAssessmentDeliveryAction({
          variables: {
            organizationId: organization.id,
            complianceProgramId,
            targetIds,
            action,
          },
          onCompleted: () => {
            const successMsg = getBatchCalibrationSuccess(action, label);
            successNotificationVar({
              title: successMsg,
            });
          },
        });
      }
    },
    [
      notCalibratedTargetIds,
      notStartedTargetIds,
      calibratedNotApprovedTargetIds,
      complianceProgram,
      complianceProgramId,
      confirmBatchAction,
      batchAssessmentDeliveryAction,
      setBatchActionConfirmationMessage,
      setBatchActionConfirmationTitle,
      organization.id,
      label,
    ],
  );

  const reportDropdownActions = useMemo(() => {
    const isWorking =
      isSavingReportingView ||
      isDeletingReportingView ||
      isExportingCsv ||
      isRunningBatchDeliveryAction;

    const batchActionMenu = compact([
      (complianceProgram?.calibration ===
        ComplianceProgramCalibrationType.PostCalibration ||
        complianceProgram?.calibration ===
          ComplianceProgramCalibrationType.PreCalibration) && {
        label: `For all displayed ${label("review", {
          pluralize: true,
        })}, set the status to "Calibrated"`,
        onClick: () =>
          handleBatchCalibrationAction(BatchCalibrationAction.MarkAsCalibrated),
        disabled: notCalibratedTargetIds.length === 0,
      },
      complianceProgram?.calibration ===
        ComplianceProgramCalibrationType.PostCalibration && {
        label: `For all displayed ${label("review", {
          pluralize: true,
        })} set to "Calibrated", set the status to "Approved for Delivery"`,
        onClick: () =>
          handleBatchCalibrationAction(
            BatchCalibrationAction.ApproveCalibrated,
          ),
        disabled: calibratedNotApprovedTargetIds.length === 0,
      },
      complianceProgram?.calibration ===
        ComplianceProgramCalibrationType.PostCalibration && {
        label: `For all displayed ${label("review", {
          pluralize: true,
        })}, set the status to "Approved for Delivery"`,
        onClick: () =>
          handleBatchCalibrationAction(BatchCalibrationAction.ApproveAll),
        disabled: notStartedTargetIds.length === 0,
      },
    ]);

    const batchActionsAvailable =
      batchActionMenu.filter((item) => !item.disabled).length > 0;

    return removeConsecutiveDividers(
      compact([
        batchActionsAvailable && {
          type: "submenu",
          label: "Batch actions",
          options: batchActionMenu,
        },
        {
          type: "divider",
        },
        viewOptions.id && {
          label: "Duplicate this view",
          onClick: handleDuplicateView,
          disabled: isWorking,
        },
        viewOptions.id &&
          viewOptions.isDirty && {
            label: "Save as new view",
            onClick: handleSaveAsNewView,
            disabled: isWorking,
          },
        viewOptions.id && {
          label: "Delete this view",
          onClick: handleDeleteView,
          disabled: isWorking,
        },
        {
          type: "divider",
        },
        isInReporting && {
          type: "link",
          label: `View ${label("review", { capitalize: true })} Progress`,
          to: getProgramUrl(complianceProgramId, { subPath: "/progress" }),
          disabled: isWorking,
        },
        {
          type: "divider",
        },
        complianceProgram?.canReadDetails.permission &&
          filteredTableRows.length > 0 && {
            label: "Export to CSV",
            onClick: handleExportCsv,
            disabled: isWorking,
          },
      ]),
    );
  }, [
    complianceProgram,
    complianceProgramId,
    filteredTableRows,
    handleDeleteView,
    handleDuplicateView,
    handleExportCsv,
    handleSaveAsNewView,
    handleBatchCalibrationAction,
    isExportingCsv,
    isSavingReportingView,
    isDeletingReportingView,
    isRunningBatchDeliveryAction,
    isInReporting,
    viewOptions,
    notStartedTargetIds,
    calibratedNotApprovedTargetIds,
    notCalibratedTargetIds,
    label,
  ]);

  const context = useMemo(
    () => ({
      complianceProgramId,
      complianceProgram,
      chartLabels,
      viewOptions,
      calibrationGroups: {
        data: calibrationGroups,
        showColumn: calibrationGroupsEnabled && calibrationGroups.length > 0,
      },
      userRows,
    }),
    [
      complianceProgram,
      complianceProgramId,
      chartLabels,
      viewOptions,
      calibrationGroups,
      calibrationGroupsEnabled,
      userRows,
    ],
  );

  return (
    <PerformanceAssessmentReportContext.Provider value={context}>
      <div>
        <CalibrationGroupNoEmployeesInfoDialog />
        {isLoadingProgram ? (
          <div className="flex-1 flex justify-center p-10">
            <Loading>Loading...</Loading>
          </div>
        ) : (
          <>
            <BatchActionConfirmationDialog />
            <ReportingViewDeletionConfirm />
            {showCalibrationGroupsModal !== null && (
              <CalibrationGroupsModal
                complianceProgramId={complianceProgramId}
                isCreatingNew={
                  showCalibrationGroupsModal === CalibrationGroupsModalView.FORM
                }
                calibrationGroups={calibrationGroups}
                filteredUsers={filteredTableRows.map((row) => row.user)}
                onClose={handleCloseCalibrationGroupsModal}
              />
            )}
            <div
              className={`px-8 pb-4 z-30 bg-content-background sticky top-0 ${
                embedded ? "pt-6" : ""
              }`}
            >
              {headerContent(
                <div className="flex-1 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ComboboxGeneric
                      className="max-w-[180px]"
                      prefix="View"
                      options={savedViewOptions}
                      disabled={
                        isSavingReportingView || isDeletingReportingView
                      }
                      onChangeValue={handleChangeSavedView}
                      value={
                        savedViewOptions.find(
                          ({ value }) => value === viewOptions.id,
                        ) ?? null
                      }
                      placeholder="Default"
                    />
                    {calibrationGroupsEnabled &&
                      calibrationGroups.length > 0 && (
                        <ComboboxGeneric
                          prefix="Calibration Group"
                          options={calibrationGroupOptions}
                          onClearValue={() =>
                            setSelectedCalibrationGroupId(null)
                          }
                          clearable
                          onChangeValue={(opt) =>
                            setSelectedCalibrationGroupId(opt.value)
                          }
                          value={
                            calibrationGroupOptions.find(
                              ({ value }) =>
                                value === selectedCalibrationGroupId,
                            ) ?? null
                          }
                          placeholder="All Groups"
                        />
                      )}
                  </div>
                  <div className="flex items-center">
                    {calibrationGroupsEnabled && isAdmin && (
                      <Dropdown
                        align="end"
                        options={[
                          {
                            label: "View All Calibration Groups",
                            onClick: handleShowCalibrationGroupsList,
                            disabled: calibrationGroups.length === 0,
                          },
                          {
                            label: "Create New Calibration Group",
                            onClick: handleShowCalibrationGroupCreateNew,
                          },
                        ]}
                      >
                        <Button
                          disabled={isLoadingCalibrationGroups}
                          className="flex items-center"
                          theme={buttonTheme.iconGray}
                          aria-label="Calibration Groups"
                        >
                          <TopicflowIcon type="users" className="h-4 w-4" />
                        </Button>
                      </Dropdown>
                    )}
                    <ViewOptionsButton
                      highlighted={viewOptions.isDirty || !!viewOptions.id}
                      disabled={
                        isSavingReportingView || isDeletingReportingView
                      }
                      onClick={handleShowViewOptionsSidebar}
                    />
                    {viewOptions.isDirty && (
                      <>
                        <Button
                          size={ButtonSize.large}
                          theme={buttonTheme.text}
                          disabled={
                            isSavingReportingView || isDeletingReportingView
                          }
                          text="Reset"
                          className="mr-2"
                          onClick={handleResetViewOptionsToBaseline}
                        />
                        <Button
                          disabled={
                            isSavingReportingView || isDeletingReportingView
                          }
                          size={ButtonSize.large}
                          theme={buttonTheme.primary}
                          className="flex items-center gap-1 mr-2"
                          onClick={handleSaveExistingView}
                          text="Save View"
                        />
                      </>
                    )}
                    <Dropdown
                      align="end"
                      disabled={reportDropdownActions.length === 0}
                      options={reportDropdownActions}
                    />
                  </div>
                </div>,
              )}
            </div>
            <div
              className={`absolute inset-0 overflow-y-scroll px-8 mb-12 ${
                embedded ? "mt-20" : "mt-32"
              }`}
            >
              {complianceProgram && (
                <PerformanceAssessmentChart
                  questions={questions}
                  onChangeAnswerFilterList={(answerFilterList) =>
                    handleUpdateViewOptions({
                      filters: {
                        ...viewOptions.filters,
                        list: updateFilters(
                          viewOptions.filters.list,
                          AssessmentReportFilterType.ANSWER,
                          answerFilterList,
                        ),
                      },
                    })
                  }
                />
              )}

              {complianceProgram && assessmentTemplate && (
                <PerformanceAssessmentTable
                  calibrationGroupsEnabled={calibrationGroupsEnabled}
                  questions={questions}
                  filteredTableRows={filteredTableRows}
                  talentIndicatorQuestions={talentIndicatorQuestions}
                  showViewOptionsSidebar={!!showViewOptionsSidebar}
                  hiddenRowCount={userRows.length - filteredTableRows.length}
                  recentAnswerQuestionIds={recentAnswerQuestionIds}
                  prevAnswersByAssessmentId={prevAnswersByAssessmentId}
                  onUpdateSelectedCalibrationGroup={
                    setSelectedCalibrationGroupId
                  }
                  onUpdateViewOptions={handleUpdateViewOptions}
                  onCloseViewOptionsSidebar={handleHideViewOptionsSidebar}
                  onUpdateChartLabels={setChartLabels}
                  onResetViewToBaseline={handleResetViewOptionsToBaseline}
                  onResetViewToEmpty={handleResetViewOptionsToEmpty}
                  onSaveView={handleSaveExistingView}
                  onSaveAsNewView={handleSaveAsNewView}
                  onDuplicateView={handleDuplicateView}
                  onDeleteView={handleDeleteView}
                  onCreateCalibrationGroup={handleShowCalibrationGroupCreateNew}
                />
              )}
            </div>
          </>
        )}
      </div>
    </PerformanceAssessmentReportContext.Provider>
  );
};

export default PerformanceAssessmentReport;
